import { cloneDeep } from "lodash";

import NodeState from "../types/nodeState";

/**
 * Get a tree, set the state on a node with the given
 * nodeId then return the tree.
 */
export const setNodeState = (tree, nodeId, nextState) => {
  const node = tree.find((node) => node.id === nodeId);
  node.nodeState = nextState;

  return tree;
};

/**
 * Get a tree and a nodeId. Count how many checked nodes
 * the subtree starting at nodeId has. Set the state
 * depending on the number of checked nodes.
 */
export const recomputeIndeterminate = (tree, nodeId) => {
  const subtree = findSubtree(tree, nodeId);
  if (!subtree?.length) {
    return tree;
  }

  const total = subtree.length;
  const noChecked = subtree.filter(
    (node) => node.nodeState === NodeState.CHECKED
  ).length;

  if (noChecked === total) {
    return setNodeState(tree, nodeId, NodeState.CHECKED);
  } else if (noChecked === 0) {
    return setNodeState(tree, nodeId, NodeState.UNCHECKED);
  }

  return setNodeState(tree, nodeId, NodeState.INDETERMINATE);
};

/**
 * Get the subtree of a node, excluding the node itself,
 * as a flat tree. Works on flat trees only.
 */
export const findSubtree = (tree, nodeId) => {
  const children = tree.filter((node) => node.parentId === nodeId);
  if (!children?.length) {
    return [];
  }

  const indirectDescendants = [];
  for (const child of children) {
    const subSubtree = findSubtree(tree, child.id);
    indirectDescendants.push(...subSubtree);
  }

  return [...children, ...indirectDescendants];
};

/**
 * Given a flat tree as created by {@link flattenTree} and a
 * node - toggle the state of the node and handle the
 * secondary effects of that action.
 *
 * @param {Array<Object>} treeState flattened tree
 * @param {Object} currentNode the node currently toggled
 * @returns {Array<Object>} new flattened tree
 */
export const toggleNode = (treeState, currentNode) => {
  const { nodeState: currentState, id: nodeId } = currentNode;
  if (!currentState || !nodeId) {
    throw new Error(
      `No state or ID found for node (id: '${nodeId}', state: '${currentState}').`
    );
  }

  let nextTreeState = cloneDeep(treeState);

  /**
   * [ ] || [-] -> [x]
   */
  if (
    currentState === NodeState.UNCHECKED ||
    currentState === NodeState.INDETERMINATE
  ) {
    nextTreeState = setNodeState(nextTreeState, nodeId, NodeState.CHECKED);

    const children = treeState.filter((node) => node.parentId === nodeId);
    for (const childNode of children) {
      nextTreeState = setNodeState(
        nextTreeState,
        childNode.id,
        NodeState.CHECKED
      );
    }
  }

  /**
   * [x] -> [ ]
   */
  if (currentState === NodeState.CHECKED) {
    nextTreeState = setNodeState(nextTreeState, nodeId, NodeState.UNCHECKED);

    const children = treeState.filter((node) => node.parentId === nodeId);
    for (const childNode of children) {
      nextTreeState = setNodeState(
        nextTreeState,
        childNode.id,
        NodeState.UNCHECKED
      );
    }
  }

  if (currentNode?.parentId) {
    nextTreeState = recomputeIndeterminate(nextTreeState, currentNode.parentId);
  }

  return nextTreeState;
};
